/**
 * Adapter — bridges the existing FormulaCalculator host contract
 * (value: latex string, onChange: (latex) => void) to the new
 * MathEditor (which is token-based).
 *
 * Token → LaTeX is implemented in tokens.ts; LaTeX → tokens is
 * handled by the minimal parser in `latexToTokens.ts`. Anything the
 * parser can't model is surfaced as-is in the Source tab so the user
 * can finish editing the raw LaTeX (no silent "fake-letter" tokens).
 *
 * State-leak guard: the MathEditor seeds its `tokens` state from
 * `initialTokens` once at mount and never looks again. So if a
 * single adapter instance is reused across two different equation
 * blocks (the modal is kept mounted while the host swaps `value`),
 * the editor sticks with the first block's tokens — and every block
 * the user opens looks like the first one they edited.
 *
 * We solve that here by tracking the last LaTeX we emitted to the
 * host. When the incoming `value` no longer matches it, an external
 * source (a different equation block, a remote sync, an undo) put
 * fresh content in front of us; we bump `mountKey` to force the
 * MathEditor to fully remount with the new initial tokens.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MathEditor } from "./MathEditor";
import { equationToLatex, type Token } from "./tokens";
import { latexToTokens } from "./latexToTokens";
import { log } from "./logger";

export interface MathEditorAdapterProps {
  /** Current LaTeX (source-of-truth). Parsed to tokens via the
   *  minimal parser; anything unsupported jumps to the Source tab. */
  value?: string;
  /** Emit LaTeX whenever tokens change. */
  onChange: (latex: string) => void;
  /** Equation label (e.g. `eq:homotopy-def`). */
  label?: string;
  /** Breadcrumb shown in the header. */
  breadcrumb?: string;
  /** Block index (e.g. `3.4.1`). */
  blockIndex?: string;
  /** Called on Done in the header. */
  onClose?: () => void;
  /** Optional explicit-save callback wired to the host's document
   *  save. Surfaces the Save button + Autosave dropdown in the
   *  MathEditor header when provided. */
  onSaveToServer?: (latex: string) => Promise<void>;
}

export function MathEditorAdapter({
  value, onChange, label, breadcrumb, blockIndex, onClose, onSaveToServer,
}: MathEditorAdapterProps) {
  /**
   * The most-recent LaTeX we emitted to the host. The host normally
   * stores it and may re-render us with `value` set back to it; we
   * use the ref to recognise that round-trip and skip a remount. A
   * `value` that *doesn't* match this ref came from somewhere else
   * (different equation block, undo, remote sync) and is treated as
   * an external change.
   */
  const lastEmittedRef = useRef<string>(value ?? "");
  /** Key bumped on every external value change to force the
   *  MathEditor to remount with fresh initial tokens. */
  const [mountKey, setMountKey] = useState(0);

  // Detect external changes. Skip the initial render — we already
  // pass the right initialTokens to the first mount.
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      lastEmittedRef.current = value ?? "";
      return;
    }
    if ((value ?? "") !== lastEmittedRef.current) {
      log.info("host", "external value change — remounting MathEditor", {
        previousLatex: lastEmittedRef.current,
        nextLatex: value ?? "",
      });
      lastEmittedRef.current = value ?? "";
      setMountKey((k) => k + 1);
    }
  }, [value]);

  // Parse the current LaTeX into tokens for the editor's initial
  // mount. Re-computed when `mountKey` changes so a remount picks
  // up fresh tokens. We don't re-parse on every keystroke — only on
  // explicit remount.
  const initial = useMemo<Token[]>(() => {
    const src = value ?? "";
    if (!src.trim()) {
      log.info("host", "MathEditor mount: empty initial value");
      return [];
    }
    const parsed = latexToTokens(src);
    log.info("host", parsed.ok ? "MathEditor mount: parsed LaTeX cleanly" : "MathEditor mount: parsed LaTeX (partial)", {
      latex: src, tokenCount: parsed.tokens.length, unparsed: parsed.unparsed,
    });
    return parsed.tokens;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mountKey]);

  const handleChange = useCallback((tokens: Token[]) => {
    const next = equationToLatex(tokens);
    if (next !== lastEmittedRef.current) {
      lastEmittedRef.current = next;
      onChange(next);
    }
  }, [onChange]);

  const handleDone = useCallback((_latex: string) => {
    onClose?.();
  }, [onClose]);

  return (
    <MathEditor
      key={mountKey}
      initialTokens={initial}
      initialCursor={initial.length}
      label={label}
      breadcrumb={breadcrumb}
      blockIndex={blockIndex}
      onChange={handleChange}
      onDone={handleDone}
      onSaveToServer={onSaveToServer}
    />
  );
}
